import React from 'react';
import {
  Box,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
  useTheme,
  alpha
} from '@mui/material';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { ApiKey } from '../services/ApiKeysService';

interface TablePaginationProps {
  page: number;
  rowsPerPage: number;
  totalItems: number;
  paginatedItems: ApiKey[];
  serverPagination: boolean;
  handlePageChange: (newPage: number) => void;
  handleChangeRowsPerPage: (event: SelectChangeEvent<number>) => void;
  getPaginationInfo: () => string;
}

const TablePagination: React.FC<TablePaginationProps> = ({
  page,
  rowsPerPage,
  totalItems,
  paginatedItems,
  serverPagination,
  handlePageChange,
  handleChangeRowsPerPage,
  getPaginationInfo
}) => {
  const theme = useTheme();

  const disablePrevious = page === 0;
  const disableNext = serverPagination
    ? page * rowsPerPage + paginatedItems.length >= totalItems
    : (page + 1) * rowsPerPage >= totalItems;

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        px: 2.5,
        py: 2,
        borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
        backgroundColor: theme.palette.background.default
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
          Rows per page:
        </Typography>
        <Select
          value={rowsPerPage}
          onChange={handleChangeRowsPerPage}
          variant="standard"
          sx={{
            '& .MuiSelect-select': {
              py: 0
            }
          }}
        >
          {[10, 25, 50, 100].map(option => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
          {getPaginationInfo()}
        </Typography>
        <Box sx={{ display: 'flex' }}>
          <IconButton
            onClick={() => handlePageChange(page - 1)}
            disabled={disablePrevious}
            sx={{
              color: disablePrevious ? 'text.disabled' : 'text.secondary',
              '&:hover': {
                backgroundColor: disablePrevious ? undefined : alpha(theme.palette.primary.main, 0.08)
              }
            }}
          >
            <KeyboardArrowLeftIcon />
          </IconButton>
          <IconButton
            onClick={() => handlePageChange(page + 1)}
            disabled={disableNext}
            sx={{
              color: disableNext ? 'text.disabled' : 'text.secondary',
              '&:hover': {
                backgroundColor: disableNext ? undefined : alpha(theme.palette.primary.main, 0.08)
              }
            }}
          >
            <KeyboardArrowRightIcon />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

export default TablePagination;
